import { toast } from "@devhop/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  CatalogBrandCreateInput,
  CatalogBrandListQuery,
  CatalogBrandUpdateInput,
  CatalogProductCreateInput,
  CatalogProductListQuery,
  CatalogProductUpdateInput,
  CatalogSkuAlternateCreateInput,
  CatalogSkuCreateInput,
  CatalogSkuListQuery,
  CatalogSkuSpecCreateInput,
  CatalogSkuSupersessionCreateInput,
  CatalogSkuUpdateInput,
  EpcDiagramCreateInput,
  EpcDiagramPartCreateInput,
  EpcDiagramPartUpdateInput,
  EpcDiagramUpdateInput,
  EpcGroupCreateInput,
  EpcGroupUpdateInput,
  VehicleChassisRangeCreateInput,
  VehicleChassisRangeUpdateInput,
  VehicleMakeCreateInput,
  VehicleMakeListQuery,
  VehicleMakeUpdateInput,
  VehicleModelCreateInput,
  VehicleModelListQuery,
  VehicleModelUpdateInput,
  VehicleModelCodeCreateInput,
  VehicleModelCodeUpdateInput,
  VehicleConfigurationCodeAssignmentsSetInput,
  VehicleConfigurationCreateInput,
  VehicleConfigurationListQuery,
  VehicleConfigurationUpdateInput,
} from "@/modules/catalog/domain/contracts";
import { catalogApi } from "./client";

export const catalogKeys = {
  all: ["catalog"] as const,
  productsList: (q: CatalogProductListQuery) =>
    ["catalog", "products", "list", q] as const,
  productDetail: (id: string) => ["catalog", "products", "detail", id] as const,
  skuMedia: (skuId: string) => ["catalog", "skus", skuId, "media"] as const,
  productSkus: (productId: string, q?: Partial<CatalogSkuListQuery>) =>
    ["catalog", "products", productId, "skus", q ?? {}] as const,
  skuFitments: (skuId: string) =>
    ["catalog", "skus", skuId, "fitments"] as const,
  brands: (q?: Partial<CatalogBrandListQuery>) =>
    ["catalog", "brands", q ?? {}] as const,
  vehicleMakes: (q?: VehicleMakeListQuery) =>
    ["catalog", "vehicle-makes", q ?? {}] as const,
  vehicleMakeDetail: (id: string) =>
    ["catalog", "vehicle-makes", "detail", id] as const,
  vehicleModels: (q?: VehicleModelListQuery) =>
    ["catalog", "vehicle-models", q ?? {}] as const,
  vehicleConfigurations: (q?: VehicleConfigurationListQuery) =>
    ["catalog", "vehicle-configurations", q ?? {}] as const,
  vehicleConfigurationDetail: (configurationId: string) =>
    ["catalog", "vehicle-configurations", configurationId, "detail"] as const,
  vehicleModelCodes: (modelId: string) =>
    ["catalog", "vehicle-models", modelId, "codes"] as const,
  configurationCodeAssignments: (configurationId: string) =>
    [
      "catalog",
      "vehicle-configurations",
      configurationId,
      "code-assignments",
    ] as const,
  chassisRanges: (configurationId: string) =>
    [
      "catalog",
      "vehicle-configurations",
      configurationId,
      "chassis-ranges",
    ] as const,
  skuSupersessions: (skuId: string) =>
    ["catalog", "skus", skuId, "supersessions"] as const,
  skuAlternates: (skuId: string) =>
    ["catalog", "skus", skuId, "alternates"] as const,
  skuSpecs: (skuId: string) => ["catalog", "skus", skuId, "specs"] as const,
  epcGroups: (configurationId: string) =>
    ["catalog", "vehicle-configurations", configurationId, "epc-groups"] as const,
  epcDiagrams: (groupId: string) =>
    ["catalog", "epc-groups", groupId, "diagrams"] as const,
  epcDiagramParts: (diagramId: string) =>
    ["catalog", "epc-diagrams", diagramId, "parts"] as const,
  pendingFitmentSkus: (q: { limit?: number; offset?: number }) =>
    ["catalog", "pending-fitment", q] as const,
  candidates: (q: { limit?: number; offset?: number }) =>
    ["catalog", "candidates", q] as const,
};

export function useCatalogProductsQuery(
  query: Partial<CatalogProductListQuery> = {},
) {
  const q: CatalogProductListQuery = {
    limit: query.limit ?? 20,
    offset: query.offset ?? 0,
    status: query.status,
    categoryId: query.categoryId,
    brandId: query.brandId,
    type: query.type,
    search: query.search,
  };
  return useQuery({
    queryKey: catalogKeys.productsList(q),
    queryFn: () => catalogApi.listProducts(q),
  });
}

export function useCatalogProductQuery(id: string) {
  return useQuery({
    queryKey: catalogKeys.productDetail(id),
    queryFn: () => catalogApi.getProduct(id),
    enabled: !!id,
  });
}

export function useSkuMediaQuery(skuId: string) {
  return useQuery({
    queryKey: catalogKeys.skuMedia(skuId),
    queryFn: () => catalogApi.listSkuMedia(skuId),
    enabled: !!skuId,
  });
}

export function useAddSkuMedia(skuId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (url: string) => catalogApi.addSkuMedia(skuId, url),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: catalogKeys.skuMedia(skuId) });
    },
  });
}

export function useDeleteSkuMedia(skuId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (mediaId: string) =>
      catalogApi.deleteSkuMedia(skuId, mediaId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: catalogKeys.skuMedia(skuId) });
    },
  });
}

export function useReorderSkuMedia(skuId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (mediaIds: string[]) =>
      catalogApi.reorderSkuMedia(skuId, mediaIds),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: catalogKeys.skuMedia(skuId) });
    },
  });
}

export function useCatalogProductSkusQuery(
  productId: string,
  query: Partial<CatalogSkuListQuery> = {},
) {
  const q = {
    limit: query.limit ?? 20,
    offset: query.offset ?? 0,
    search: query.search,
    condition: query.condition,
  };
  return useQuery({
    queryKey: catalogKeys.productSkus(productId, q),
    queryFn: () => catalogApi.listSkus(productId, q),
    enabled: !!productId,
  });
}

export function useCatalogSkuFitmentsQuery(skuId: string) {
  return useQuery({
    queryKey: catalogKeys.skuFitments(skuId),
    queryFn: () => catalogApi.listFitments(skuId),
    enabled: !!skuId,
  });
}

export function useCatalogBrandsQuery(
  query: Partial<CatalogBrandListQuery> = {},
) {
  const q = {
    limit: query.limit ?? 20,
    offset: query.offset ?? 0,
    search: query.search,
  };
  return useQuery({
    queryKey: catalogKeys.brands(q),
    queryFn: () => catalogApi.listBrands(q),
  });
}

export function useCatalogVehicleMakesQuery(
  query: Partial<VehicleMakeListQuery> = {},
) {
  const q = {
    limit: query.limit ?? 20,
    offset: query.offset ?? 0,
    search: query.search,
  };
  return useQuery({
    queryKey: catalogKeys.vehicleMakes(q),
    queryFn: () => catalogApi.listVehicleMakes(q),
  });
}

export function useCatalogVehicleMakeQuery(id: string) {
  return useQuery({
    queryKey: catalogKeys.vehicleMakeDetail(id),
    queryFn: () => catalogApi.getVehicleMake(id),
    enabled: !!id,
  });
}

export function useCatalogVehicleModelsQuery(
  query: Partial<VehicleModelListQuery> = {},
) {
  const q = {
    makeId: query.makeId,
    limit: query.limit ?? 20,
    offset: query.offset ?? 0,
  };
  return useQuery({
    queryKey: catalogKeys.vehicleModels(q),
    queryFn: () => catalogApi.listVehicleModels(q),
  });
}

export function useCatalogVehicleConfigurationsQuery(
  query: Partial<VehicleConfigurationListQuery> = {},
) {
  const q = {
    makeId: query.makeId,
    modelId: query.modelId,
    limit: query.limit ?? 20,
    offset: query.offset ?? 0,
  };
  return useQuery({
    queryKey: catalogKeys.vehicleConfigurations(q),
    queryFn: () => catalogApi.listVehicleConfigurations(q),
  });
}

export function useCreateCatalogProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CatalogProductCreateInput) =>
      catalogApi.createProduct(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: catalogKeys.all });
      toast.success("ເພີ່ມສິນຄ້າສຳເລັດ");
    },
  });
}

export function useUpdateCatalogProduct(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CatalogProductUpdateInput) =>
      catalogApi.updateProduct(id, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: catalogKeys.productDetail(id) });
      qc.invalidateQueries({ queryKey: catalogKeys.all });
      toast.success("ແກ້ໄຂສິນຄ້າສຳເລັດ");
    },
  });
}

export function useDeleteCatalogProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => catalogApi.deleteProduct(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: catalogKeys.all });
    },
  });
}

export function useMergeCatalogProducts() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      sourceProductId: string;
      targetProductId: string;
    }) => catalogApi.mergeProducts(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: catalogKeys.all });
    },
  });
}

export function useCreateCatalogSku(productId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Omit<CatalogSkuCreateInput, "catalogProductId">) =>
      catalogApi.createSku(productId, input),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: ["catalog", "products", productId, "skus"],
      });
      qc.invalidateQueries({ queryKey: catalogKeys.productDetail(productId) });
      toast.success("ເພີ່ມ SKU ສຳເລັດ");
    },
  });
}

export function useUpdateCatalogSku(productId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: CatalogSkuUpdateInput }) =>
      catalogApi.updateSku(id, input),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: ["catalog", "products", productId, "skus"],
      });
      qc.invalidateQueries({ queryKey: catalogKeys.productDetail(productId) });
      toast.success("ແກ້ໄຂ SKU ສຳເລັດ");
    },
  });
}

export function useDeleteCatalogSku(productId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => catalogApi.deleteSku(id),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: ["catalog", "products", productId, "skus"],
      });
      qc.invalidateQueries({ queryKey: catalogKeys.productDetail(productId) });
      toast.success("ລຶບ SKU ສຳເລັດ");
    },
  });
}

export function useCreateCatalogFitment(skuId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      vehicleConfigurationId: string;
      fitmentNote?: string | null;
      quantityPerVehicle?: number | null;
      applicabilityCode?: string | null;
    }) => catalogApi.createFitment(skuId, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: catalogKeys.skuFitments(skuId) });
      toast.success("ເພີ່ມ Fitment ສຳເລັດ");
    },
  });
}

export function useDeleteCatalogFitment(skuId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => catalogApi.deleteFitment(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: catalogKeys.skuFitments(skuId) });
      toast.success("ລຶບ Fitment ສຳເລັດ");
    },
  });
}

export function useCreateCatalogBrand() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CatalogBrandCreateInput) =>
      catalogApi.createBrand(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["catalog", "brands"] });
      toast.success("ເພີ່ມແບຣນດ໌ສຳເລັດ");
    },
  });
}

export function useUpdateCatalogBrand() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      input,
    }: {
      id: string;
      input: CatalogBrandUpdateInput;
    }) => catalogApi.updateBrand(id, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["catalog", "brands"] });
      qc.invalidateQueries({ queryKey: catalogKeys.all });
      toast.success("ແກ້ໄຂແບຣນດ໌ສຳເລັດ");
    },
  });
}

export function useDeleteCatalogBrand() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => catalogApi.deleteBrand(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["catalog", "brands"] });
      qc.invalidateQueries({ queryKey: catalogKeys.all });
      toast.success("ລຶບແບຣນດ໌ສຳເລັດ");
    },
  });
}

export function useCreateVehicleMake() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: VehicleMakeCreateInput) =>
      catalogApi.createVehicleMake(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["catalog", "vehicle-makes"] });
      toast.success("ເພີ່ມຍີ່ຫໍ້ລົດສຳເລັດ");
    },
  });
}

export function useUpdateVehicleMake() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      input,
    }: {
      id: string;
      input: VehicleMakeUpdateInput;
    }) => catalogApi.updateVehicleMake(id, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["catalog", "vehicle-makes"] });
      qc.invalidateQueries({ queryKey: catalogKeys.vehicleModels() });
      qc.invalidateQueries({ queryKey: catalogKeys.vehicleConfigurations() });
      toast.success("ແກ້ໄຂຍີ່ຫໍ້ລົດສຳເລັດ");
    },
  });
}

export function useDeleteVehicleMake() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => catalogApi.deleteVehicleMake(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["catalog", "vehicle-makes"] });
      qc.invalidateQueries({ queryKey: catalogKeys.vehicleModels() });
      qc.invalidateQueries({ queryKey: catalogKeys.vehicleConfigurations() });
      toast.success("ລຶບຍີ່ຫໍ້ລົດສຳເລັດ");
    },
  });
}

export function useCreateVehicleModel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: VehicleModelCreateInput) =>
      catalogApi.createVehicleModel(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: catalogKeys.vehicleModels() });
      qc.invalidateQueries({ queryKey: catalogKeys.vehicleConfigurations() });
      toast.success("ເພີ່ມຮຸ່ນລົດສຳເລັດ");
    },
  });
}

export function useUpdateVehicleModel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      input,
    }: {
      id: string;
      input: VehicleModelUpdateInput;
    }) => catalogApi.updateVehicleModel(id, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: catalogKeys.vehicleModels() });
      qc.invalidateQueries({ queryKey: catalogKeys.vehicleConfigurations() });
      toast.success("ແກ້ໄຂຮຸ່ນລົດສຳເລັດ");
    },
  });
}

export function useDeleteVehicleModel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => catalogApi.deleteVehicleModel(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: catalogKeys.vehicleModels() });
      qc.invalidateQueries({ queryKey: catalogKeys.vehicleConfigurations() });
      toast.success("ລຶບຮຸ່ນລົດສຳເລັດ");
    },
  });
}

export function useCreateVehicleConfiguration() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: VehicleConfigurationCreateInput) =>
      catalogApi.createVehicleConfiguration(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: catalogKeys.vehicleConfigurations() });
      toast.success("ເພີ່ມ configuration ສຳເລັດ");
    },
  });
}

export function useUpdateVehicleConfiguration() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      input,
    }: {
      id: string;
      input: VehicleConfigurationUpdateInput;
    }) => catalogApi.updateVehicleConfiguration(id, input),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: catalogKeys.vehicleConfigurations() });
      qc.invalidateQueries({
        queryKey: catalogKeys.vehicleConfigurationDetail(id),
      });
      qc.invalidateQueries({
        queryKey: ["catalog", "vehicle-configurations", id],
      });
      toast.success("ແກ້ໄຂ configuration ສຳເລັດ");
    },
  });
}

export function useDeleteVehicleConfiguration() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => catalogApi.deleteVehicleConfiguration(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: catalogKeys.vehicleConfigurations() });
      toast.success("ລຶບ configuration ສຳເລັດ");
    },
  });
}

export function useVehicleConfigurationDetailQuery(configurationId: string) {
  return useQuery({
    queryKey: catalogKeys.vehicleConfigurationDetail(configurationId),
    queryFn: () => catalogApi.getVehicleConfigurationDetail(configurationId),
    enabled: !!configurationId,
  });
}

export function useVehicleModelCodesQuery(modelId: string) {
  return useQuery({
    queryKey: catalogKeys.vehicleModelCodes(modelId),
    queryFn: () => catalogApi.listVehicleModelCodes(modelId),
    enabled: !!modelId,
  });
}

export function useConfigurationCodeAssignmentsQuery(configurationId: string) {
  return useQuery({
    queryKey: catalogKeys.configurationCodeAssignments(configurationId),
    queryFn: () => catalogApi.getConfigurationCodeAssignments(configurationId),
    enabled: !!configurationId,
  });
}

/** @deprecated Use useVehicleModelCodesQuery */
export function useVehicleConfigurationCodesQuery(configurationId: string) {
  return useQuery({
    queryKey: [
      "catalog",
      "vehicle-configurations",
      configurationId,
      "codes",
    ] as const,
    queryFn: () => catalogApi.listVehicleConfigurationCodes(configurationId),
    enabled: !!configurationId,
  });
}

export function useChassisRangesQuery(configurationId: string) {
  return useQuery({
    queryKey: catalogKeys.chassisRanges(configurationId),
    queryFn: () => catalogApi.listChassisRanges(configurationId),
    enabled: !!configurationId,
  });
}

export function useSkuSupersessionsQuery(skuId: string) {
  return useQuery({
    queryKey: catalogKeys.skuSupersessions(skuId),
    queryFn: () => catalogApi.listSkuSupersessions(skuId),
    enabled: !!skuId,
  });
}

export function useSkuAlternatesQuery(skuId: string) {
  return useQuery({
    queryKey: catalogKeys.skuAlternates(skuId),
    queryFn: () => catalogApi.listSkuAlternates(skuId),
    enabled: !!skuId,
  });
}

export function useSkuSpecsQuery(skuId: string) {
  return useQuery({
    queryKey: catalogKeys.skuSpecs(skuId),
    queryFn: () => catalogApi.listSkuSpecs(skuId),
    enabled: !!skuId,
  });
}

export function useEpcGroupsQuery(configurationId: string) {
  return useQuery({
    queryKey: catalogKeys.epcGroups(configurationId),
    queryFn: () => catalogApi.listEpcGroups(configurationId),
    enabled: !!configurationId,
  });
}

export function useEpcDiagramsQuery(groupId: string) {
  return useQuery({
    queryKey: catalogKeys.epcDiagrams(groupId),
    queryFn: () => catalogApi.listEpcDiagrams(groupId),
    enabled: !!groupId,
  });
}

export function useEpcDiagramPartsQuery(diagramId: string) {
  return useQuery({
    queryKey: catalogKeys.epcDiagramParts(diagramId),
    queryFn: () => catalogApi.listEpcDiagramParts(diagramId),
    enabled: !!diagramId,
  });
}

export function useCreateVehicleModelCode(modelId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Omit<VehicleModelCodeCreateInput, "modelId">) =>
      catalogApi.createVehicleModelCode(modelId, input),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: catalogKeys.vehicleModelCodes(modelId),
      });
      qc.invalidateQueries({ queryKey: catalogKeys.vehicleConfigurations() });
      qc.invalidateQueries({
        queryKey: ["catalog", "vehicle-configurations"],
      });
      toast.success("ເພີ່ມ code ສຳເລັດ");
    },
  });
}

export function useUpdateVehicleModelCode(modelId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      input,
    }: {
      id: string;
      input: VehicleModelCodeUpdateInput;
    }) => catalogApi.updateVehicleModelCode(id, input),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: catalogKeys.vehicleModelCodes(modelId),
      });
      qc.invalidateQueries({ queryKey: catalogKeys.vehicleConfigurations() });
      toast.success("ແກ້ໄຂ code ສຳເລັດ");
    },
  });
}

export function useDeleteVehicleModelCode(modelId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => catalogApi.deleteVehicleModelCode(id),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: catalogKeys.vehicleModelCodes(modelId),
      });
      qc.invalidateQueries({ queryKey: catalogKeys.vehicleConfigurations() });
      qc.invalidateQueries({
        queryKey: ["catalog", "vehicle-configurations"],
      });
      toast.success("ລຶບ code ສຳເລັດ");
    },
  });
}

export function useSetConfigurationCodeAssignments(configurationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: VehicleConfigurationCodeAssignmentsSetInput) =>
      catalogApi.setConfigurationCodeAssignments(configurationId, input),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: catalogKeys.configurationCodeAssignments(configurationId),
      });
      qc.invalidateQueries({
        queryKey: catalogKeys.vehicleConfigurationDetail(configurationId),
      });
      qc.invalidateQueries({ queryKey: catalogKeys.vehicleConfigurations() });
      toast.success("ບັນທຶກການເລືອກ code ສຳເລັດ");
    },
  });
}

export function useCreateVehicleChassisRange(configurationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (
      body: Omit<VehicleChassisRangeCreateInput, "vehicleConfigurationId">,
    ) =>
      catalogApi.createVehicleChassisRange({
        ...body,
        vehicleConfigurationId: configurationId,
      }),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: catalogKeys.chassisRanges(configurationId),
      });
      qc.invalidateQueries({
        queryKey: catalogKeys.vehicleConfigurationDetail(configurationId),
      });
      toast.success("ເພີ່ມຊ່ວງ chassis ສຳເລັດ");
    },
  });
}

export function useUpdateVehicleChassisRange(configurationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      input,
    }: {
      id: string;
      input: VehicleChassisRangeUpdateInput;
    }) => catalogApi.updateVehicleChassisRange(id, input),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: catalogKeys.chassisRanges(configurationId),
      });
      toast.success("ອັດເດັດ ຊ່ວງ chassis ສຳເລັດ");
    },
  });
}

export function useDeleteVehicleChassisRange(configurationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => catalogApi.deleteVehicleChassisRange(id),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: catalogKeys.chassisRanges(configurationId),
      });
      qc.invalidateQueries({
        queryKey: catalogKeys.vehicleConfigurationDetail(configurationId),
      });
      toast.success("ລຶບ ຊ່ວງ chassis ສຳເລັດ");
    },
  });
}

export function useCreateSkuSupersession(skuId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CatalogSkuSupersessionCreateInput) =>
      catalogApi.createSkuSupersession(skuId, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: catalogKeys.skuSupersessions(skuId) });
      toast.success("ເພີ່ມ supersession ສຳເລັດ");
    },
  });
}

export function useDeleteSkuSupersession(skuId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => catalogApi.deleteSkuSupersession(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: catalogKeys.skuSupersessions(skuId) });
      toast.success("ລຶບ supersession ສຳເລັດ");
    },
  });
}

export function useCreateSkuAlternate(skuId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Omit<CatalogSkuAlternateCreateInput, "catalogSkuId">) =>
      catalogApi.createSkuAlternate(skuId, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: catalogKeys.skuAlternates(skuId) });
      toast.success("ເພີ່ມທາງເລືອກສຳເລັດ");
    },
  });
}

export function useDeleteSkuAlternate(skuId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => catalogApi.deleteSkuAlternate(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: catalogKeys.skuAlternates(skuId) });
      toast.success("ລຶບທາງເລືອກສຳເລັດ");
    },
  });
}

export function useCreateSkuSpec(skuId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Omit<CatalogSkuSpecCreateInput, "catalogSkuId">) =>
      catalogApi.createSkuSpec(skuId, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: catalogKeys.skuSpecs(skuId) });
      toast.success("ເພີ່ມ spec ສຳເລັດ");
    },
  });
}

export function useDeleteSkuSpec(skuId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => catalogApi.deleteSkuSpec(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: catalogKeys.skuSpecs(skuId) });
      toast.success("ລຶບ spec ສຳເລັດ");
    },
  });
}

export function useCreateEpcGroup(configurationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: EpcGroupCreateInput) =>
      catalogApi.createEpcGroup(input),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: ["catalog", "vehicle-configurations", configurationId],
      });
      toast.success("ເພີ່ມກຸ່ມ EPC ສຳເລັດ");
    },
  });
}

export function useUpdateEpcGroup(configurationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: EpcGroupUpdateInput }) =>
      catalogApi.updateEpcGroup(id, input),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: ["catalog", "vehicle-configurations", configurationId],
      });
      toast.success("ອັດເດັດ ກຸ່ມ EPC ສຳເລັດ");
    },
  });
}

export function useDeleteEpcGroup(configurationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => catalogApi.deleteEpcGroup(id),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: ["catalog", "vehicle-configurations", configurationId],
      });
      toast.success("ລຶບກຸ່ມ EPC ສຳເລັດ");
    },
  });
}

export function useCreateEpcDiagram(groupId: string, configurationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: EpcDiagramCreateInput) =>
      catalogApi.createEpcDiagram(input),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: catalogKeys.epcDiagrams(groupId),
      });
      qc.invalidateQueries({
        queryKey: ["catalog", "vehicle-configurations", configurationId],
      });
      toast.success("ເພີ່ມແຜນຮູບສຳເລັດ");
    },
  });
}

export function useUpdateEpcDiagram(groupId: string, configurationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: EpcDiagramUpdateInput }) =>
      catalogApi.updateEpcDiagram(id, input),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: catalogKeys.epcDiagrams(groupId),
      });
      qc.invalidateQueries({
        queryKey: ["catalog", "vehicle-configurations", configurationId],
      });
      toast.success("ອັດເດັດແຜນຮູບສຳເລັດ");
    },
  });
}

export function useDeleteEpcDiagram(groupId: string, configurationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => catalogApi.deleteEpcDiagram(id),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: catalogKeys.epcDiagrams(groupId),
      });
      qc.invalidateQueries({
        queryKey: ["catalog", "vehicle-configurations", configurationId],
      });
      qc.invalidateQueries({ queryKey: ["catalog", "epc-diagrams"] });
      toast.success("ລຶບແຜນຮູບສຳເລັດ");
    },
  });
}

export function useCreateEpcDiagramPart(diagramId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: EpcDiagramPartCreateInput) =>
      catalogApi.createEpcDiagramPart(input),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: catalogKeys.epcDiagramParts(diagramId),
      });
      toast.success("ເພີ່ມ callout ສຳເລັດ");
    },
  });
}

export function useUpdateEpcDiagramPart(diagramId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      input,
    }: {
      id: string;
      input: EpcDiagramPartUpdateInput;
    }) => catalogApi.updateEpcDiagramPart(id, input),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: catalogKeys.epcDiagramParts(diagramId),
      });
      toast.success("ອັດເດັດ callout ສຳເລັດ");
    },
  });
}

export function useDeleteEpcDiagramPart(diagramId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => catalogApi.deleteEpcDiagramPart(id),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: catalogKeys.epcDiagramParts(diagramId),
      });
      toast.success("ລຶບ callout ສຳເລັດ");
    },
  });
}

export function usePendingFitmentSkusQuery(
  query: { limit?: number; offset?: number } = {},
) {
  const params = { limit: query.limit ?? 20, offset: query.offset ?? 0 };
  return useQuery({
    queryKey: catalogKeys.pendingFitmentSkus(params),
    queryFn: () => catalogApi.listPendingFitmentSkus(params),
  });
}

export function useRejectPendingProvisionMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (catalogSkuId: string) =>
      catalogApi.rejectPendingProvision(catalogSkuId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: catalogKeys.all });
    },
  });
}

export function useCatalogCandidatesQuery(
  query: { limit?: number; offset?: number } = {},
) {
  const params = { limit: query.limit ?? 20, offset: query.offset ?? 0 };
  return useQuery({
    queryKey: catalogKeys.candidates(params),
    queryFn: () => catalogApi.listCatalogCandidates(params),
  });
}

export function useApproveCatalogCandidateMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (catalogProductId: string) =>
      catalogApi.approveCatalogCandidate(catalogProductId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: catalogKeys.all });
      toast.success("ອະນຸມັດ candidate ແລ້ວ");
    },
  });
}

export function useRejectCatalogCandidateMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (catalogProductId: string) =>
      catalogApi.rejectCatalogCandidate(catalogProductId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: catalogKeys.all });
      toast.success("ປະຕິເສດ candidate ແລ້ວ");
    },
  });
}

export function useDownloadVehicleImportTemplateMutation() {
  return useMutation({
    mutationFn: (format: "xlsx" | "csv") =>
      catalogApi.downloadVehicleImportTemplate(format),
  });
}

export function usePreviewVehicleImportMutation() {
  return useMutation({
    mutationFn: (file: File) => catalogApi.previewVehicleImport(file),
  });
}

export function useCommitVehicleImportMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => catalogApi.commitVehicleImport(file),
    onSuccess: (result) => {
      if (result.ok) {
        qc.invalidateQueries({ queryKey: catalogKeys.all });
        toast.success("ນຳເຂົ້າຂໍ້ມູນລົດສຳເລັດ");
      }
    },
  });
}
